// Generic admin panel for editing a page's Visual Editor sections. Powered
// by PageSectionsEditor. Used by Home, Glossary intro, and Bookmarks intro.
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PencilLine, Eye, LayoutTemplate, Check } from "lucide-react";
import PageSectionsEditor from "@/components/PageSectionsEditor";
import { defaultHomeSections, defaultGlossaryIntroSections, defaultBookmarksIntroSections } from "@/components/sectionTypes";
import HomePresetsPanel from "@/components/admin/HomePresetsPanel";

const PAGE_META = {
  home: {
    title: "Home page",
    previewUrl: "/",
    descPlaceholder: "Describe this home page layout — e.g. 'Standard evergreen layout — hero, pathways, latest blogs, glossary teaser. Ideal for the default site experience.'",
  },
  glossary_intro: {
    title: "Glossary intro",
    previewUrl: "/glossary",
    descPlaceholder: "Describe this intro layout — visible to admins only.",
  },
  bookmarks_intro: {
    title: "Bookmarks intro",
    previewUrl: "/bookmarks",
    descPlaceholder: "Describe this intro layout — visible to admins only.",
  },
  tools_and_metrics: {
    title: "Tools and Metrics",
    previewUrl: "/tools-and-metrics",
    descPlaceholder: "Describe what this page is for — visible to admins only. Leave blank until you're ready to fill the page with tools, calculators, or metrics.",
  },
};

export default function PageSectionsPanel({ pageKey }) {
  const meta = PAGE_META[pageKey];
  const [sections, setSections] = useState(null);
  const [layoutDesc, setLayoutDesc] = useState("");
  const [savedDesc, setSavedDesc] = useState("");
  const [savingDesc, setSavingDesc] = useState(false);
  const [descJustSaved, setDescJustSaved] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const descSaveTimer = useRef(null);

  const load = () =>
    api.get(`/site-settings/page-sections/${pageKey}`)
       .then(r => {
         setSections(r.data.sections || []);
         const d = r.data.description || "";
         setLayoutDesc(d);
         setSavedDesc(d);
       })
       .catch(() => setSections([]));
  useEffect(() => { load(); }, [pageKey]);

  // Debounced description save so admins can type freely.
  useEffect(() => {
    if (sections === null) return;
    if (layoutDesc === savedDesc) return;
    if (descSaveTimer.current) clearTimeout(descSaveTimer.current);
    descSaveTimer.current = setTimeout(async () => {
      setSavingDesc(true);
      try {
        await api.put(`/site-settings/page-sections/${pageKey}`, { description: layoutDesc });
        setSavedDesc(layoutDesc);
        setDescJustSaved(true);
        setTimeout(() => setDescJustSaved(false), 1600);
      } catch { /* silent — user will retry */ }
      finally { setSavingDesc(false); }
    }, 700);
    return () => descSaveTimer.current && clearTimeout(descSaveTimer.current);
  }, [layoutDesc, savedDesc, sections, pageKey]);

  const save = async (next) => {
    await api.put(`/site-settings/page-sections/${pageKey}`, { sections: next });
    setSections(next);
  };

  if (!sections) return <div className="mt-6 text-slate-500">Loading…</div>;

  return (
    <Card className="border-slate-200 mt-6">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <LayoutTemplate className="w-4 h-4 text-sky-700"/>
          {meta.title} · Visual Editor
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
              Description
              {savingDesc && <span className="text-slate-400 normal-case">saving…</span>}
              {descJustSaved && !savingDesc && (
                <span className="text-emerald-600 normal-case inline-flex items-center gap-1"><Check className="w-3 h-3"/>saved</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <a href={meta.previewUrl} target="_blank" rel="noopener noreferrer"
                 className="text-xs uppercase tracking-widest text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">
                <Eye className="w-3.5 h-3.5"/>Preview page
              </a>
              <Button data-testid={`page-sections-open-${pageKey}`}
                      onClick={() => setEditorOpen(true)}
                      className="bg-sky-700 hover:bg-sky-600">
                <PencilLine className="w-4 h-4 mr-2"/>Open Visual Editor
              </Button>
            </div>
          </div>
          <Textarea
            data-testid={`page-sections-description-${pageKey}`}
            value={layoutDesc}
            onChange={e => setLayoutDesc(e.target.value.slice(0, 800))}
            placeholder={meta.descPlaceholder}
            rows={3}
            className="bg-white"
          />
          <div className="text-[11px] text-slate-500 flex items-center justify-between">
            <span>Only visible to admins. Great for jotting down what this layout is for or when it was last refreshed.</span>
            <span className="tabular-nums">{layoutDesc.length}/800</span>
          </div>
        </div>

        {pageKey === "home" && (
          <HomePresetsPanel liveSections={sections} onApplied={load}/>
        )}
      </CardContent>

      <PageSectionsEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        title={`Edit ${meta.title.toLowerCase()}`}
        resourceKey={`page:${pageKey}`}
        initialSections={
          sections.length === 0
            ? (pageKey === "home" ? defaultHomeSections()
              : pageKey === "glossary_intro" ? defaultGlossaryIntroSections()
              : pageKey === "bookmarks_intro" ? defaultBookmarksIntroSections()
              : [])
            : sections
        }
        onSave={save}
      />
    </Card>
  );
}
